import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { containsNetworkError } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getAutoConvertionInfo } from '@confluence/editor-conversion/entry-points/getAutoConvertionInfo.ts';
import type { ShareContentType } from '@confluence/share/entry-points/ShareButton.tsx';
import { fg } from '@confluence/feature-gating';

type UseIsPageAutoConvertedType = {
	contentId: string;
	contentType: ShareContentType;
};

// used only for determining whether the "revert to legacy editor" menu item should show in overflow menu of view/edit page and blogposts
export const useIsPageOrBlogAutoConverted = ({
	contentId,
	contentType,
}: UseIsPageAutoConvertedType): boolean => {
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data: contentPropertiesData, error } = useQuery(
		gql`
			query useIsPageOrBlogAutoConvertedQuery(
				$contentId: ID
				$versionOverride: Int
				$status: [String]
			) {
				content(id: $contentId, version: $versionOverride, status: $status) {
					nodes {
						id
						type
						properties(key: "editor") {
							nodes {
								id
								key
								value
							}
						}
						operations {
							operation
							targetType
						}
						body {
							dynamic {
								representation
							}
						}
						metadata {
							frontend {
								fabricEditorSupported
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				status: expValEquals('confluence_auto_convert_legacy_draft_fix_fe', 'isEnabled', true)
					? ['draft', 'current']
					: null,
			},
			// skip if is not page or blogpost as the revert to legacy editor menu item can only show for those content types
			skip:
				!(contentType === 'page' || contentType === 'blogpost') &&
				fg('confluence_frontend_reintroduce_revert_editor_item'),
			// cache-and-network fixes the stale cache issue where isAutoConverted returns wrong value after publishing
			fetchPolicy: fg('confluence_frontend_fix_revert_btn_tiny')
				? 'cache-and-network'
				: 'cache-first',
		},
	);

	if (!fg('confluence_frontend_reintroduce_revert_editor_item')) {
		return false;
	}

	if (error) {
		if (isUnauthorizedError(error) || containsNetworkError(error)) {
			markErrorAsHandled(error);
		} else {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.BACKBONE,
			});
		}
		return false;
	}

	if (contentPropertiesData) {
		const { isAutoConverted } = getAutoConvertionInfo(contentPropertiesData?.content?.nodes?.[0]);
		const isNewPage = contentPropertiesData?.content?.nodes?.length === 0;

		return !!isAutoConverted && !isNewPage;
	}

	return false;
};
